/**
 * 應用程式配置
 *
 * 集中管理所有設定值，包括：
 * - API 端點 URL
 * - OAuth 客戶端 ID
 * - 環境判斷邏輯
 */

/**
 * 判斷是否為本地開發環境
 */
function isLocalDevelopment(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * 主要配置物件
 */
export const CONFIG = {
  // ========================================================================
  // API 端點設定
  // ========================================================================

  /**
   * 生產環境的後端 API URL
   * 🔒 安全性：優先使用環境變數,避免硬編碼暴露 Project ID
   *
   * 設定方式：
   * - 本地開發：在 .env 中設定 VITE_API_BASE_URL
   * - Cloud Run：在部署時設定環境變數
   */
  PRODUCTION_API_URL: import.meta.env.VITE_API_BASE_URL ||
                      'https://raiseplan-api-598627707162.asia-east1.run.app',

  /** 本地開發的後端 API URL */
  LOCAL_API_URL: import.meta.env.VITE_LOCAL_API_URL || 'http://localhost:8195',

  /**
   * API 基礎 URL（根據環境自動選擇）
   */
  get API_BASE_URL(): string {
    return isLocalDevelopment() ? this.LOCAL_API_URL : this.PRODUCTION_API_URL;
  },

  /**
   * 座位規劃 API 端點
   */
  get PLANNER_API_URL(): string {
    return `${this.API_BASE_URL}/api/v1/plan-furniture`;
  },

  /**
   * 非同步規劃 API 端點
   */
  get PLANNER_ASYNC_API_URL(): string {
    return `${this.API_BASE_URL}/api/v1/plan-furniture-async`;
  },

  /**
   * 任務狀態查詢 API 端點
   */
  getTaskStatusUrl(taskId: string): string {
    return `${this.API_BASE_URL}/api/v1/tasks/${taskId}`;
  },

  /**
   * IP 檢查 API 端點
   */
  get IP_CHECK_URL(): string {
    return `${this.API_BASE_URL}/api/v1/ip-check`;
  },

  /**
   * 圖片預處理 API 端點
   */
  get PREPROCESS_API_URL(): string {
    return `${this.API_BASE_URL}/api/v1/preprocess`;
  },

  /**
   * 尺寸偵測 API 端點
   */
  get DETECT_DIMENSIONS_URL(): string {
    return `${this.API_BASE_URL}/api/v1/detect-dimensions`;
  },

  // ========================================================================
  // SSO 認證設定
  // ========================================================================

  /**
   * Google OAuth Client ID
   *
   * 安全說明：
   * - OAuth Client ID 設計為公開，可以安全地暴露在前端程式碼中
   * - 這不是 Client Secret，不會造成安全風險
   * - 請確保在 Google Cloud Console 中設定嚴格的 Redirect URI 白名單
   * - 僅允許來自您的域名的請求
   *
   * 環境建議：
   * - 生產環境應使用環境變數注入（透過 Vite 的 import.meta.env）
   * - 本地開發可以硬編碼
   */
  GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID || '598627707162-8l3j7pav7jq0jpulm6vvu9sqhd0fajro.apps.googleusercontent.com',

  /**
   * Microsoft OAuth Client ID
   *
   * 安全說明：同上，可以安全地公開
   * 請確保在 Azure AD 中設定嚴格的 Redirect URI
   */
  MICROSOFT_CLIENT_ID: import.meta.env.VITE_MICROSOFT_CLIENT_ID || '4333353a-3a3d-4863-9ab4-79d9da5a3096',

  /**
   * Microsoft Authority URL
   * 使用 /common 端點支援個人和組織帳號
   */
  MICROSOFT_AUTHORITY: 'https://login.microsoftonline.com/common',

  /**
   * 認證驗證 API 端點
   */
  get AUTH_VERIFY_URL(): string {
    return `${this.API_BASE_URL}/api/v1/auth/verify`;
  },

  /**
   * 取得當前使用者資訊 API 端點
   */
  get AUTH_ME_URL(): string {
    return `${this.API_BASE_URL}/api/v1/auth/me`;
  },

  /**
   * 登出 API 端點
   */
  get AUTH_LOGOUT_URL(): string {
    return `${this.API_BASE_URL}/api/v1/auth/logout`;
  },

  // ========================================================================
  // 儲存方案 API
  // ========================================================================

  /**
   * 儲存方案列表 API 端點
   */
  get SAVED_PLANS_URL(): string {
    return `${this.API_BASE_URL}/api/v1/saved-plans`;
  },

  /**
   * 取得特定儲存方案 API 端點
   */
  getSavedPlanUrl(planId: string): string {
    return `${this.API_BASE_URL}/api/v1/saved-plans/${planId}`;
  },
} as const;

/**
 * 環境資訊
 */
export const ENV = {
  /** 是否為本地開發環境 */
  IS_LOCAL: isLocalDevelopment(),

  /** 是否為生產環境 */
  IS_PRODUCTION: !isLocalDevelopment(),
} as const;
